import { Component, OnInit, ViewChild, OnDestroy, HostListener, Signal, signal } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { RequestService } from '../services/request.service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { ToolboxService } from '../toolbox/config/toolBox.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { InteriorObject } from '../models/interior-object.model';
import { take, takeUntil } from 'rxjs/operators';
import { EditorCanvasComponent } from '../editor-canvas/editor-canvas.component';
import { EditorService } from '../services/editor.service';
import { Command } from '../models/commands/command.model';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { Variant } from '../models/variant.model';
import { ModalDialog } from '../Modal/modal';
import { LayerEnum } from '../models/enums/LayerEnum';
import { FloorEnum } from '../models/enums/FloorEnum';
import { ActivatedRoute, Router } from '@angular/router';
import { WaitForModalResult } from '../helper/modalHelper';
import { dynamicLocaleCompareWithOptions } from '../helper/sortFunctions';
import { EditorInformation } from '../models/EditorInformation.model';
import { DropdownModel } from '../models/dropdown.model';
import { AddVariantComponent } from '../variant/variant-list/add-variant/add-variant.component';
import { environment } from 'src/environments/environment';
import { MasterData } from '../models/masterData.model';
import { changeEditorVariantFloorStructureToVariantFloorStructure } from '../helper/changeEditorVariantFloorStructureToVariantFloorStructure';
import { CloneService } from '../services/clone.service';
import { FeatureManager } from '../editor-canvas/feature-manager';
import { FeatureName } from '../editor-canvas/feature-name-constants';
import { DefaultFeature } from '../editor-canvas/default-feature';
import { MeasurementFeature } from '../editor-canvas/measurement-feature';
import { LoaderService } from '../services/loader.service';

const FloorSource: DropdownModel[] = [
  { id: FloorEnum.First, name: "Deck 1" },
  { id: FloorEnum.Second, name: "Deck 2" },
];
const LayerSource: DropdownModel[] = [
  { id: LayerEnum.ground, name: "Floor" },
  { id: LayerEnum.ceiling, name: "Ceiling" },
];
const SnappingModeSource: DropdownModel[] = [
  { id: 0, name: "None" },
  { id: 1, name: "Edge-Snaping" },
  { id: 2, name: "Seat-Divider" },
];

const newlineseperator = '\n\n';
const verticalscroll = 'Scroll Up / Down | Mouse Wheel';
const horizontalscroll = 'Scroll Left / Right | Ctrl + Alt + Mouse Wheel';
const zoom = 'Zoom In / Out | Ctrl + Mouse Wheel';
const snap = 'Enable Object Snapping | Press Spacebar During Dragging';
const rotation = 'Rotate An Object | Right Mouse Click On The Object';
const save = 'Save |  Ctrl + S';
const saveas = 'Save As | Ctrl + Alt + S';
const propertywindow = 'Toggle Property Window | Ctrl + P';
const containervisibilitywindow = 'Toggle Container Visibility Window | Ctrl + Alt + V';
const toolbox = 'Toggle Toolbox | Ctrl + O';
const commandwindow = 'Show Command Info | Ctrl + I';
const copyPaste = 'Copy | Ctrl + C';
const commandinfomessage = verticalscroll + newlineseperator + horizontalscroll + newlineseperator + zoom + newlineseperator + snap + newlineseperator + rotation + newlineseperator + copyPaste
  + newlineseperator + save + newlineseperator + saveas + newlineseperator + propertywindow + newlineseperator + containervisibilitywindow + newlineseperator +
  toolbox + newlineseperator + commandwindow;

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  providers: [],
  animations: [
    trigger(
      'enterAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('500ms', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('500ms', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ]
    )
  ]
})
export class EditorComponent implements OnInit, OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();
  toolBoxConfig: ToolboxService;
  variantList: Observable<Variant[]>;
  selectedFloor: number;
  selectedLayer: number;
  selectedSnapingMode: number;
  public layerSource: Array<DropdownModel> = new Array<DropdownModel>();
  public floorSource: Array<DropdownModel> = new Array<DropdownModel>();
  public snapingModeSource: Array<DropdownModel> = new Array<DropdownModel>();
  backButtonModalMessage: string = "Please verify that you have saved the variant, otherwise the unsaved modifications will be lost!";
  private routeSub: Subscription;
  public editorInformation: EditorInformation;
  public selectedVariant: number;
  private variantIdFromRoute: number;
  private waggonIdFromRoute: number;
  private skipCanDeactivateOnSave: boolean = false;
  private skipCanDeactivateOnSaveAs: boolean = false;
  private isCommandInforDialogOpen = false;
  private featureManager: FeatureManager;
  validationStatus: string = "Variant name length needs to be between 1 and maximum 100 characters long"
  disableSaveButton: boolean = false;
  public environment = environment;
  public masterData: Signal<MasterData>;
  floorContainerList: any[];
  activeNavRightWindow: string = null;
  @HostListener('window:resize', ['$event'])
  handleResize(event) {
    this.editorCanvasComponent.resizeStage();
  }
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    this.handleKeyBindings(event);
  }

  @ViewChild('navright', { static: false })
  navright: MatSidenav;
  @ViewChild('toolboxnav', { static: false })
  toolboxnav: MatSidenav;
  @ViewChild(EditorCanvasComponent, { static: false })
  editorCanvasComponent: EditorCanvasComponent;

  constructor(
    private route: ActivatedRoute,
    private requestService: RequestService,
    public editorService: EditorService,
    private dialog: MatDialog,
    private router: Router,
    private cloneService: CloneService,
    private loaderService: LoaderService
  ) {
    this.routeSub = this.route.params.subscribe(params => {
      this.variantIdFromRoute = parseInt(params['variantId'].toString());
      this.waggonIdFromRoute = parseInt(params['waggonId'].toString());

      this.selectedVariant = this.variantIdFromRoute;
      this.loadEditorInformation(this.selectedVariant);
      this.resetDisabledButton();
    });
  }

  ngOnInit() {
    // Leave those subscriptions here since ngOnInit will be called 1 time when navigating to the editor.
    // Moving this code to ctor will cause multiple subscriptions due to pipe.(takeUntil(this.unsubscribe$)),
    // which will only be terminated when navigating away from the editor in ngOnDestroy. 
    this.requestService.getMasterData()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(masterData => {
        this.masterData = signal(masterData);
      });
    this.createToolboxConfig();
    this.editorServiceSubscriptions();
    this.toolboxConfigSubscriptions();
    this.setLayerSource();
    this.setSnapingModeSource();
  }

  setLayerSource() {
    this.layerSource = LayerSource;
  }

  setFloorSource() {
    if (this.editorService.currentVariant.floors.length === 1) {
      this.floorSource = FloorSource.filter(x => x.id === FloorEnum.First);
      return;
    }
    this.floorSource = FloorSource;
  }

  setSnapingModeSource() {
    this.snapingModeSource = SnappingModeSource;
  }

  editorServiceSubscriptions() {
    this.editorService.getCommand()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (command: Command) => {
          if (command == null || command == undefined) {
            return;
          }
          command.execute(this.editorCanvasComponent);
        });

    this.editorService.currentFloor.pipe(takeUntil(this.unsubscribe$)).subscribe((val: number) => {
      this.selectedFloor = val;
    });
    this.editorService.currentLayer.pipe(takeUntil(this.unsubscribe$)).subscribe((val: number) => {
      this.selectedLayer = val;
    });
    this.editorService.currentSnapingMode.pipe(takeUntil(this.unsubscribe$)).subscribe((val: number) => {
      this.selectedSnapingMode = val;
    });
  };

  toolboxConfigSubscriptions() {
    this.toolBoxConfig.currentItem.
      pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (item: InteriorObject) => {
          if (item == null) {
            return;
          }
          this.editorService.setCurrentEditorObjectFromInteriorObject(this.cloneService.deepClone(item));
        });
  };

  loadEditorInformation(variantId: number) {
    this.requestService.GetEditorInformation(variantId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((next: EditorInformation) => {
        if (next) {
          this.editorInformation = next;
          this.setVariant(this.editorInformation.variant);
          this.setFloorSource();
          this.setVariantList(this.editorInformation.variantList);
        }
        else {
          //
        }
      });
  }

  setVariantList(variants: DropdownModel[]) {
    return variants.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
  }

  createToolboxConfig(): void {
    this.toolBoxConfig = new ToolboxService;
    this.toolBoxConfig.sourceHttpCall = this.getToolboxSource;
    this.toolBoxConfig.hideDeselectButton = false;
    this.toolBoxConfig.listenToLayer = true;
    this.toolBoxConfig.currentItem = new BehaviorSubject<InteriorObject>(null);
    this.toolBoxConfig.currentLayer = this.editorService.currentLayer;
  }

  resetDisabledButton() {
    this.disableSaveButton = false;
  }

  getToolboxSource(service: RequestService): Observable<InteriorObject[]> {
    const obs = service.getToolBoxItems();
    return obs;
  }

  showEditorCommandInfo() {
    this.isCommandInforDialogOpen = true;

    let dialogRef = this.dialog.open(ModalDialog, {
      data: {
        title: 'Editor Command Information',
        message: commandinfomessage,
        saveButtonText: 'Okay',
        showCancel: false
      }
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      this.isCommandInforDialogOpen = false;
    });
  }

  setVariant(variant: Variant) {
    // map response to be consumed by konva
    this.editorService.setCurrentVariant(variant);

    this.editorCanvasComponent.createStage();
    this.editorCanvasComponent.initLayers();
    this.editorCanvasComponent.setZoomingToStage();
    this.editorCanvasComponent.setScrollingToStage();

    this.editorService.initFloorLayerAndSnapingMode();
    // set selected layer
    this.editorCanvasComponent.setSelectedLayer(this.editorService.currentLayer.value);
    this.floorContainerList = this.editorService.getFloorContainers(this.selectedFloor);

    this.editorCanvasComponent.scaleUntilContentIsCentered();

    this.editorCanvasComponent.setLayerPositions();

    this.editorCanvasComponent.setCurrentScale();

    this.editorCanvasComponent.clearCanvas();
    this.editorCanvasComponent.redrawEditor();
    this.editorCanvasComponent.changeLayer();

    this.initializeAndRegisterCanvasFeatures();

    // collission detection needs to be executed here as well in case of "save" so the objects indicating a collission don't lose their red colour
    this.editorCanvasComponent.globalCollissionDetection();
  }

  private initializeAndRegisterCanvasFeatures() {
    this.featureManager = new FeatureManager(this.editorCanvasComponent, FeatureName.defaultFeature);
    this.featureManager.registerFeature(FeatureName.defaultFeature, new DefaultFeature());
    this.featureManager.registerFeature(FeatureName.measurementFeature, new MeasurementFeature());

    this.activateDefaultFeature();
  }

  onMeasurementFeatureStateChanged(state: boolean) {
    this.editorService.setMeasurementFunctionState(state);
    let featureName = state ? FeatureName.measurementFeature : FeatureName.defaultFeature;
    this.featureManager.activateFeature(featureName);
  }

  activateDefaultFeature() {
    this.featureManager.activateFeature(FeatureName.defaultFeature);
  }

  activateMeasurementFeature() {
    this.featureManager.activateFeature(FeatureName.measurementFeature);
  }

  toggleNavRight(windowname) {
    if (this.activeNavRightWindow === windowname) {
      // If the same button is clicked twice, close the sidenav
      this.navright.toggle();
      this.activeNavRightWindow = null;
    } else {
      // Switch the content without closing the sidenav
      this.activeNavRightWindow = windowname;
      if (!this.navright.opened) {
        this.navright.open();
      }
    }
  }

  changeVariant(variantId: number) {
    if (variantId !== this.variantIdFromRoute) {
      this.navigateTo(variantId);
    }
  }

  navigateTo(variantId: number) {
    this.router.navigate(['/editor', this.waggonIdFromRoute, variantId]);
  }

  canDeactivate() {
    // this dialog appears when layout is changed via dropdown,refresh is pressed,navigate back is clicked or browser close is clicked
    // on save this dialog will not be called
    if (!this.skipCanDeactivateOnSave && !this.skipCanDeactivateOnSaveAs) {
      const dialogRef = this.dialog.open(ModalDialog, {
        data: {
          title: 'Warning',
          message: this.backButtonModalMessage,
          showCancel: true,
          cancelButtonText: 'Cancel',
          saveButtonText: 'Confirm'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (!result) {
          // this needs to be done when route changes via dropdown appear and the rout change is canceled,this will keep the selected variant in sync
          this.selectedVariant = this.variantIdFromRoute;
        }
      });
      return WaitForModalResult(dialogRef);
    }
    // this dialog appears when save as is clicked
    // it gives the user the control wether to change to the new layout or stay at the current
    // since this case is different a different dialog needs to be shown, otherwise the save reminder appears here like navigating back etc.
    if (!this.skipCanDeactivateOnSave && this.skipCanDeactivateOnSaveAs) {
      this.skipCanDeactivateOnSaveAs = false;
      const dialogRef = this.dialog.open(ModalDialog, {
        data: {
          title: 'Information',
          message: 'The new layout is now saved, do you want to switch to the new layout?',
          showCancel: true,
          cancelButtonText: 'Cancel',
          saveButtonText: 'Confirm'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (!result) {
          this.loadEditorInformation(this.variantIdFromRoute);
        }
      });
      return WaitForModalResult(dialogRef);
    }
  }

  changeFloor(value: number) {
    // after changing floors the editor variant (current state) needs to be mapped back to a normal variant object
    // since only one floor is present at a time on the canvas (this works like a save without actually calling the request service and is necessary to persist alread occured changes)
    this.editorService.setCurrentVariant(changeEditorVariantFloorStructureToVariantFloorStructure(this.editorService.currentVariant));
    // set the current floor value
    this.editorService.setCurrentFloor(value);
    this.floorContainerList = this.editorService.getFloorContainers(value);
    this.editorCanvasComponent.clearCanvas();
    // redraw objects on the current floor
    this.editorCanvasComponent.redrawEditor();
    // toggle layer depending on current layer 
    // needs do be done too because the draw method just places the objects to the layers
    // it doesn't take care of visibility/opacity
    this.editorCanvasComponent.changeLayer();
  }

  changeLayer(value: number) {
    // set the current layer value
    this.editorService.setCurrentLayer(value);
    // set the current layer on canvas
    this.editorCanvasComponent.setSelectedLayer(value);
    // change the layer on canvas
    this.editorCanvasComponent.changeLayer();
  }

  onFileSelected(event, floorContainer) {
    const file: File = event.target.files[0];
    // 1. Create a FileReader instance
    const reader = new FileReader();
    // 2. Add a handler for the 'onload' event
    reader.onload = (e) => {
      // 5. Get the result when the 'onload' event is triggered.
      const base64data = reader.result;
      console.log({ base64data });

      const element = floorContainer.editorObject;
      const layer = this.editorCanvasComponent.getLayer(this.editorCanvasComponent.getLayerEnumFromObject(element));
      element.changeBackgroundImage(base64data, layer);
    }
    // 3. Add a handler for the 'onerror' event
    reader.onerror = () => {
      console.log('error');
    };
    // 4. Call 'readAsDataURL' method
    reader.readAsDataURL(file);
  }

  toggleBackgroundImageContainer(floorContainer) {
    floorContainer.editorObject.toggleBackgroundImage();
  }

  toggleClassContainer(floorContainer) {
    floorContainer.editorObject.toggleClassFill(floorContainer.fillColor);
  }

  changeSnapingMode(value: number) {
    this.editorService.setCurrentSnapingMode(value);
    this.editorCanvasComponent.setSnapingMode(value);
  }

  zoomIn() {
    this.editorCanvasComponent.zooming(-8);
  }

  zoomOut() {
    this.editorCanvasComponent.zooming(8);
  }

  downloadURI(blob, name) {
    var link = document.createElement('a');
    var objURL = URL.createObjectURL(blob);
    link.download = name;
    link.href = objURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getFloorName(floorEnum: FloorEnum): string {
    return this.floorSource.filter(f => f.id == floorEnum)[0].name;
  }

  getLayerName(layerEnum: LayerEnum): string {
    return this.layerSource.filter(f => f.id == layerEnum)[0].name;
  }

  async takeLayoutScreenshotsBeforeSave() {
    this.loaderService.show();

    let measurementGroups = this.editorCanvasComponent.stage.find('.measurement-group');
    measurementGroups.forEach(g => g.destroy());

    var currentFloor = this.editorService.getCurrentFloor();
    var currentLayer = this.editorService.getCurrentLayer();

    var floor1Base64String = null;
    var floor2Base64String = null;

    var floor1CeilingBase64String = null;
    var floor2CeilingBase64String = null;

    this.editorCanvasComponent.scaleUntilContentIsCentered();

    this.editorCanvasComponent.stage.batchDraw();

    if (this.editorService.isTwoFloorVehicle()) {
      this.changeFloor(FloorEnum.First);
      this.changeLayer(LayerEnum.ground);

      let undoFloorContainersDeck1 = this.hideFloorContainersBeforeScreenshotIfCecessaryAndReturnUndoListOrEmpty();

      floor1Base64String = await this.editorCanvasComponent.stageToDataURL(this.getFloorName(FloorEnum.First), this.getLayerName(LayerEnum.ground));

      this.changeLayer(LayerEnum.ceiling);
      floor1CeilingBase64String = await this.editorCanvasComponent.stageToDataURL(this.getFloorName(FloorEnum.First), this.getLayerName(LayerEnum.ceiling));

      this.executeFloorContainerUndo(undoFloorContainersDeck1);

      this.changeFloor(FloorEnum.Second);
      this.changeLayer(LayerEnum.ground);

      let undoFloorContainersDeck2 = this.hideFloorContainersBeforeScreenshotIfCecessaryAndReturnUndoListOrEmpty();

      floor2Base64String = await this.editorCanvasComponent.stageToDataURL(this.getFloorName(FloorEnum.Second), this.getLayerName(LayerEnum.ground));

      this.changeLayer(LayerEnum.ceiling);
      floor2CeilingBase64String = await this.editorCanvasComponent.stageToDataURL(this.getFloorName(FloorEnum.Second), this.getLayerName(LayerEnum.ceiling));

      this.executeFloorContainerUndo(undoFloorContainersDeck2);

      this.changeFloor(currentFloor);
      this.changeLayer(currentLayer);
    }
    else {
      this.changeLayer(LayerEnum.ground);

      let undoFloorContainersDeck1 = this.hideFloorContainersBeforeScreenshotIfCecessaryAndReturnUndoListOrEmpty();
      
      floor1Base64String = await this.editorCanvasComponent.stageToDataURL(this.getFloorName(FloorEnum.First), this.getLayerName(LayerEnum.ground));

      this.changeLayer(LayerEnum.ceiling);
      floor1CeilingBase64String = await this.editorCanvasComponent.stageToDataURL(this.getFloorName(FloorEnum.First), this.getLayerName(LayerEnum.ceiling));

      this.executeFloorContainerUndo(undoFloorContainersDeck1);

      this.changeLayer(currentLayer);
    }

    this.editorService.currentVariant.floor1Base64String = floor1Base64String;
    this.editorService.currentVariant.floor2Base64String = floor2Base64String;

    this.editorService.currentVariant.floor1CeilingBase64String = floor1CeilingBase64String;
    this.editorService.currentVariant.floor2CeilingBase64String = floor2CeilingBase64String;

    if (!this.environment.production) {
      this.downloadURI(this.editorCanvasComponent.dataURLtoBlob(floor1Base64String), 'Floor1.png');
      this.downloadURI(this.editorCanvasComponent.dataURLtoBlob(floor1CeilingBase64String), 'FloorCeiling1.png');

      if (floor2Base64String != null) {
        this.downloadURI(this.editorCanvasComponent.dataURLtoBlob(floor2Base64String), 'Floor2.png');
      }
      if (floor2CeilingBase64String != null) {
        this.downloadURI(this.editorCanvasComponent.dataURLtoBlob(floor2CeilingBase64String), 'FloorCeiling2.png');
      }
    }

    this.loaderService.hide();
  };

  hideFloorContainersBeforeScreenshotIfCecessaryAndReturnUndoListOrEmpty() {
    let undoFloorContainerList = [];

    this.floorContainerList.forEach(floorContainer => {
      if (floorContainer.type === 'BackgroundContainer') {
        floorContainer.childs.forEach(child => {
          if (!child.editorObject.isDefaultImage() && child.editorObject.isBackgroundVisible()) {
            child.editorObject.toggleBackgroundImage();
            floorContainer.opacity = child.editorObject.opacity();
            undoFloorContainerList.push(floorContainer);
          }
        });
      }
      else {
        floorContainer.childs.forEach(child => {
          if (child.editorObject.isClassVisible()) {
            child.editorObject.toggleClassFill(child.fillColor);
            undoFloorContainerList.push(floorContainer);
          }
        });
      }
    });

    return undoFloorContainerList;
  }

  executeFloorContainerUndo(undoFloorContainerList) {
    undoFloorContainerList.forEach(floorContainer => {
      if (floorContainer.type === 'BackgroundContainer') {
        floorContainer.childs.forEach(child => {
          child.editorObject.toggleBackgroundImage();
          child.editorObject.opacity(floorContainer.opacity);
        });
      }
      else {
        floorContainer.childs.forEach(child => {
          child.editorObject.toggleClassFill(child.fillColor);
        });
      }
    });
  }

  save() {
    // skipCanDeactivateOnSave needs to be set here to differenciate between save operations
    if (!this.disableSaveButton) {
      this.skipCanDeactivateOnSave = true;
      this.beforeSaveProcesses();
    }
  };

  async beforeSaveProcesses() {
    const container = this.editorCanvasComponent.stage.container();
    container.focus();
    //remove selection so editor objects parent is the layer and not a group which would falsify the getposition on save
    this.editorCanvasComponent.removeSelectionFromAllObjects();
    // prepare layout description for screenshot
    this.editorService.setLayoutDescription(this.editorInformation.projectName, this.editorInformation.wagonName, this.editorInformation.companyName);

    // make screenshot of layout
    await this.takeLayoutScreenshotsBeforeSave();

    // check for collissions before saving
    let hasCollission = this.editorCanvasComponent.globalCollissionDetection();
    this.editorService.currentVariant.hasCollissions = hasCollission;
    if (hasCollission) {
      let dialogRef = this.dialog.open(ModalDialog, {
        data: {
          title: 'Warning',
          message: 'Colliding objects have been encountered, the layout will be saved with: "has collission flag".\n Please fix the collissions to use the layout for evaluation purpose.',
          saveButtonText: 'Okay',
          showCancel: false
        }
      });
      dialogRef.afterClosed()
        .subscribe(result => {
          // no need to check for else case
          if (result) {
            this.internalSave();
          }
        });
    }
    else {
      this.internalSave();
    }
  }

  private internalSave() {
    this.requestService.writeVariant(changeEditorVariantFloorStructureToVariantFloorStructure(this.editorService.currentVariant))
      .subscribe((resp: any) => {
        // for save process since there will be no routing on same page
        if (resp === this.selectedVariant) {
          this.skipCanDeactivateOnSave = false;
          this.loadEditorInformation(resp);
        }
        else {
          // for save as process
          this.navigateTo(resp);
        }
      });
  };

  validateVariantName(name: string) {
    if (name.length > 100 || name.length < 1) {
      this.disableSaveButton = true;
    }
    else {
      this.disableSaveButton = false;
      this.editorService.currentVariant.name = name;
    }
  }

  saveAsDialog() {
    let dialogRef = this.dialog.open(AddVariantComponent, {
      data: { title: 'Save Layout Under New Name', layoutname: '' }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data && data.state && data.layoutname !== '') {
        this.saveAs(data.layoutname);
      } else {
        // do nothing in this case
      }
    });
  }

  saveAs(layoutName: string) {
    // skipCanDeactivateOnSaveAs needs to be set here to differenciate between save operations
    this.skipCanDeactivateOnSaveAs = true;
    this.editorService.currentVariant.id = -1;
    this.editorService.currentVariant.name = layoutName;
    this.beforeSaveProcesses();
  }

  handleKeyBindings(event: KeyboardEvent): any {
    let key = event.keyCode;
    if (event.defaultPrevented) {
      return; // Do nothing if the event was already processed
    }

    if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 's') {
      this.saveAsDialog();
    } else if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'v') {
      this.toggleNavRight('visibility-control-window');
    }
    else if (event.ctrlKey && event.key.toLowerCase() === 's') {
      this.save();
    } else if (event.ctrlKey && event.key.toLowerCase() === 'i') {
      if (!this.isCommandInforDialogOpen) {
        this.showEditorCommandInfo();
      }
    } else if (event.ctrlKey && event.key.toLowerCase() === 'o') {
      this.toolboxnav.toggle();
    } else if (event.ctrlKey && event.key.toLowerCase() === 'p') {
      this.toggleNavRight('property-window');
    }

    else {
      return;
    }
    event.preventDefault();
  }

  debug() {
    let currentEditorObjects = this.editorService.getEditorObjects();
    console.log("current editor objects: ", currentEditorObjects);
    this.editorCanvasComponent.debug();
    // //remove selection so editor objects parent is the layer and not a group which would falsify the getposition on save
    // this.editorCanvasComponent.removeSelectionFromAllObjects();
    // // prepare layout description for screenshot
    // this.editorService.setLayoutDescription(this.editorInformation.projectName, this.editorInformation.wagonName, this.editorInformation.companyName);
    // // make screenshot of layout
    // this.takeLayoutScreenshotsBeforeSave();
  }

  ngOnDestroy(): void {
    // setCommand null needs to be done when component gets destroyed since there is no such as subscription handling in a service instance when it's a static instance.
    // therefor not setting the command to null would leave a reference to the editor canvas component.
    // e.g. comment the line where the command is set to null, navigate to the editor  project > wagon > variant
    // Navigate back to the variant list and navigate back to the editor it will throw a error regarding can't read addNewEditorObject of undefined.
    // which indicates that a editor-canvas component is still referenced which should have been destroyed already.
    this.editorService.resetBehaviorSubjectValues();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.routeSub.unsubscribe();
  }
}